import React, { memo, useEffect, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { AppLinearGradient as LinearGradient } from '../../components/ui/AppLinearGradient';
import { SolidCtaClickable } from '../../components/common/SolidCtaClickable';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { STACK_SCROLL_GUTTER, androidFlatSurfaceStyle } from '../../shared/theme/studio/stackScreenShell';
import {
  JOURNEY_FOOTER_CTA_HEIGHT,
  JOURNEY_FOOTER_VERTICAL_PAD,
} from './journeyLayout';

type Props = {
  colors: ThemeColors;
  continue_label: string;
  continue_disabled: boolean;
  show_back: boolean;
  /** Goal step hides footer until a choice is made (opacity 0, no touches). */
  footer_hidden?: boolean;
  back_label: string;
  on_back: () => void;
  on_continue: () => void;
};

export const JourneyWizardFooter = memo(function JourneyWizardFooter({
  colors,
  continue_label,
  continue_disabled,
  show_back,
  footer_hidden = false,
  back_label,
  on_back,
  on_continue,
}: Props) {
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const bottom_pad = Math.max(insets.bottom, appTheme.spacing.md);
  const footer_opacity = useSharedValue(footer_hidden ? 0 : 1);

  useEffect(() => {
    footer_opacity.value = withTiming(footer_hidden ? 0 : 1, {
      duration: footer_hidden ? 0 : 200,
      easing: Easing.out(Easing.cubic),
    });
  }, [footer_hidden, footer_opacity]);

  const wrap_animated_style = useAnimatedStyle(() => ({
    opacity: footer_opacity.value,
  }));

  return (
    <Animated.View
      style={[styles.wrap, { paddingBottom: bottom_pad }, wrap_animated_style]}
      pointerEvents={footer_hidden ? 'none' : 'auto'}>
      <LinearGradient
        colors={[hexToRgba(colors.background, 0) as string, hexToRgba(colors.surface, 0.98) as string]}
        style={styles.top_fade}
        pointerEvents="none"
      />
      <View style={styles.row}>
        {show_back ? (
          <View style={styles.back_slot}>
            <SolidCtaClickable
              label={back_label}
              onPress={on_back}
              variant="outline"
              size="compact"
              haptics
            />
          </View>
        ) : null}
        <View style={styles.continue_slot}>
          <SolidCtaClickable
            label={continue_label}
            onPress={on_continue}
            variant="primary"
            size="compact"
            disabled={continue_disabled}
            haptics
          />
        </View>
      </View>
    </Animated.View>
  );
});

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    wrap: {
      paddingTop: JOURNEY_FOOTER_VERTICAL_PAD,
      paddingHorizontal: STACK_SCROLL_GUTTER,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: hexToRgba(c.text, 0.1),
      backgroundColor: hexToRgba(c.surface, 0.98),
      ...androidFlatSurfaceStyle(),
    },
    top_fade: {
      position: 'absolute',
      top: -JOURNEY_FOOTER_VERTICAL_PAD,
      left: 0,
      right: 0,
      height: JOURNEY_FOOTER_VERTICAL_PAD,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      minHeight: JOURNEY_FOOTER_CTA_HEIGHT,
    },
    back_slot: {
      width: '36%',
      minWidth: 112,
    },
    continue_slot: {
      flex: 1,
    },
  });
}
